import React, { useState, useEffect } from 'react';
import { ticketAPI } from '../services/api';
import Icon from './Icon';

// Ticket type options based on user role
const getTicketTypesForRole = (role) => {
  const baseTypes = [
    { value: 'general', label: 'General question', iconName: 'chat', description: 'Any general question or inquiry' },
    { value: 'bug_report', label: 'Bug report', iconName: 'error', description: 'Report a technical issue or bug' },
    { value: 'feature_request', label: 'Feature request', iconName: 'info', description: 'Suggest a new feature or improvement' },
    { value: 'account_issue', label: 'Account issue', iconName: 'key', description: 'Login or account management problem' },
  ];
  
  const userTypes = [
    { value: 'bike_block', label: 'Blocked bike issue', iconName: 'lock', description: 'If your bike was blocked at a provider' },
    { value: 'service_dispute', label: 'Service dispute', iconName: 'service', description: 'Dispute or issue with a service job' },
    { value: 'booking_issue', label: 'Booking issue', iconName: 'calendar', description: 'Issue with scheduling or appointments' },
    { value: 'company_complaint', label: 'Company complaint', iconName: 'companies', description: 'Complaint about a service provider' },
  ];
  
  const companyTypes = [
    { value: 'user_complaint', label: 'User complaint', iconName: 'users', description: 'Complaint about user behavior' },
    { value: 'billing', label: 'Billing/payment', iconName: 'payment', description: 'Billing or payment questions' },
  ];
  
  const technicianTypes = [
    { value: 'service_dispute', label: 'Service question', iconName: 'service', description: 'Question or issue about service work' },
  ];
  
  switch (role) {
    case 'super_admin':
      return [...baseTypes, ...userTypes, ...companyTypes, ...technicianTypes];
    case 'company_admin':
      return [...baseTypes, ...companyTypes];
    case 'technician':
      return [...baseTypes, ...technicianTypes];
    case 'user':
    default:
      return [...baseTypes, ...userTypes];
  }
};

const priorityOptions = [
  { value: 'low', label: '⚪ Low', description: 'Not urgent' },
  { value: 'normal', label: '🔵 Normal', description: 'Standard handling' },
  { value: 'high', label: '🟠 High', description: 'Important, faster response' },
  { value: 'urgent', label: '🔴 Urgent', description: 'Needs immediate attention' },
];

function SupportContact({ user, darkMode = true, onTicketCreated, prefillTicket }) {
  const [ticketTypes, setTicketTypes] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [ticketType, setTicketType] = useState('general');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  
  // View state
  const [activeView, setActiveView] = useState('new'); // 'new' or 'my-tickets'
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Response state
  const [responseText, setResponseText] = useState('');
  const [submittingResponse, setSubmittingResponse] = useState(false);

  const subjectLength = subject.length;
  const descriptionLength = description.length;

  useEffect(() => {
    if (user?.role) {
      setTicketTypes(getTicketTypesForRole(user.role));
    }
    fetchMyTickets();
  }, [user]);

  useEffect(() => {
    if (!prefillTicket) return;

    const validTicketType = ticketTypes.some((type) => type.value === prefillTicket.ticketType)
      ? prefillTicket.ticketType
      : 'general';

    const validPriority = priorityOptions.some((opt) => opt.value === prefillTicket.priority)
      ? prefillTicket.priority
      : 'normal';

    setActiveView('new');
    setTicketType(validTicketType);
    setPriority(validPriority);
    setSubject(prefillTicket.subject || '');
    setDescription(prefillTicket.description || '');
    setError('');
  }, [prefillTicket, ticketTypes]);

  const fetchMyTickets = async () => {
    try {
      setLoadingTickets(true);
      const response = await ticketAPI.getMyTickets();
      setMyTickets(response.data.tickets || []);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const subj = subject.trim();
    const desc = description.trim();
    
    if (!subj) {
      setError('Subject is required');
      return;
    }
    if (!desc) {
      setError('Description is required');
      return;
    }
    if (subj.length > 255) {
      setError('Subject too long (max 255 characters)');
      return;
    }
    if (desc.length > 10000) {
      setError('Description too long (max 10000 characters)');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await ticketAPI.createTicket({
        ticket_type: ticketType,
        subject: subj,
        description: desc,
        priority
      });
      
      setSuccess('Ticket created successfully! We will respond soon.');
      setSubject('');
      setDescription('');
      setTicketType('general');
      setPriority('normal');
      fetchMyTickets();
      
      if (onTicketCreated) {
        onTicketCreated(response.data.ticket);
      }
      
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setLoadingDetails(true);
    try {
      const response = await ticketAPI.getTicket(ticket.id);
      setTicketDetails(response.data.ticket);
    } catch (err) {
      setError('Failed to load ticket details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseDetails = () => {
    setSelectedTicket(null);
    setTicketDetails(null);
    setResponseText('');
  };

  const handleAddResponse = async () => {
    if (!responseText.trim() || !ticketDetails) return;
    
    setSubmittingResponse(true);
    try {
      await ticketAPI.addResponse(ticketDetails.id, {
        message: responseText.trim()
      });
      setSuccess('Response sent');
      setResponseText('');
      handleViewTicket({ id: ticketDetails.id });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send response');
    } finally {
      setSubmittingResponse(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: { text: 'Open', color: '#60a5fa', bg: 'rgba(59, 130, 246, 0.15)' },
      in_review: { text: 'In review', color: '#fbbf24', bg: 'rgba(245, 158, 11, 0.15)' },
      waiting_response: { text: 'Waiting for your response', color: '#a78bfa', bg: 'rgba(139, 92, 246, 0.15)' },
      resolved: { text: 'Resolved', color: '#4A8C18', bg: 'rgba(26, 40, 17, 0.15)' },
      closed: { text: 'Closed', color: '#9ca3af', bg: 'rgba(107, 114, 128, 0.15)' }
    };
    return labels[status] || labels.open;
  };

  // New ticket form
  const NewTicketForm = () => (
    <form onSubmit={handleSubmit} autoComplete="off" data-lpignore="true">
      {/* Ticket Type */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Type
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
          {ticketTypes.map(type => (
            <label
              key={type.value}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '12px',
                borderRadius: '10px',
                cursor: 'pointer',
                background: ticketType === type.value 
                  ? (darkMode ? 'rgba(61, 90, 32, 0.1)' : 'rgba(61, 90, 32, 0.2)')
                  : (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'),
                border: ticketType === type.value 
                  ? '2px solid #3d5a20'
                  : (darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)'),
                transition: 'all 0.2s'
              }}
            >
              <input
                type="radio"
                name="ticketType"
                value={type.value}
                checked={ticketType === type.value}
                onChange={(e) => setTicketType(e.target.value)}
                style={{ marginTop: '3px' }}
              />
              <div>
                <div style={{ fontWeight: '500', color: darkMode ? 'white' : '#1f2937', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name={type.iconName} color={ticketType === type.value ? "nav_active" : "nav_inactive"} darkMode={darkMode} size={14} />
                  {type.label}
                </div>
                <div style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280' }}>
                  {type.description}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>
      
      {/* Priority */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Priority
        </label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {priorityOptions.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setPriority(opt.value)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: priority === opt.value ? '2px solid #3d5a20' : '1px solid rgba(255,255,255,0.1)',
                background: priority === opt.value 
                  ? (darkMode ? 'rgba(61, 90, 32, 0.1)' : 'rgba(61, 90, 32, 0.2)')
                  : 'transparent',
                color: darkMode ? 'white' : '#1f2937',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Subject */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Subject *
        </label>
        <input
          type="text"
          name="ticket-subject"
          id="ticket-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Short summary of the issue..."
          maxLength={255}
          required
          autoComplete="off"
          style={{ 
            width: '100%',
            padding: '12px 16px',
            fontSize: '16px',
            color: darkMode ? '#ffffff' : '#1a1a1a',
            background: darkMode ? 'rgba(255,255,255,0.05)' : '#ffffff',
            border: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
            borderRadius: '8px',
            outline: 'none',
            pointerEvents: 'auto',
            cursor: 'text'
          }}
        />
        <div style={{ 
          fontSize: '11px', 
          color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af',
          marginTop: '4px',
          textAlign: 'right'
        }}>
          {subjectLength}/255
        </div>
      </div>
      
      {/* Description */}
      <div style={{ marginBottom: '24px' }}>
        <label style={{ 
          display: 'block', 
          marginBottom: '8px', 
          color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151',
          fontSize: '14px',
          fontWeight: '500'
        }}>
          Detailed description *
        </label>
        <textarea
          name="ticket-description"
          id="ticket-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Include all important details about the issue..."
          className="form-input"
          style={{ 
            minHeight: '150px', 
            resize: 'vertical',
            color: darkMode ? '#ffffff' : '#1a1a1a'
          }}
          maxLength={10000}
          required
        />
        <div style={{ 
          fontSize: '11px', 
          color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af',
          marginTop: '4px',
          textAlign: 'right'
        }}>
          {descriptionLength}/10000
        </div>
      </div>
      
      {/* Submit */}
      <button
        type="submit"
        disabled={loading || subjectLength === 0 || descriptionLength === 0}
        className="btn btn-primary"
        style={{ width: '100%', padding: '14px' }}
      >
        {loading ? (
          <>
            <Icon name="loading" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Sending...
          </>
        ) : (
          <>
            <Icon name="send" color="primary" darkMode={darkMode} size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
            Submit ticket
          </>
        )}
      </button>
    </form>
  );

  // My tickets list
  const MyTicketsList = () => (
    <div>
      {loadingTickets ? (
        <div className="loading">Loading tickets...</div>
      ) : myTickets.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280'
        }}>
          <Icon name="help" color="muted" darkMode={darkMode} size={48} style={{ marginBottom: '16px' }} />
          <p>No tickets yet</p>
          <button
            onClick={() => setActiveView('new')}
            className="btn btn-primary"
            style={{ marginTop: '16px' }}
          >
            Create new ticket
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {myTickets.map(ticket => {
            const statusStyle = getStatusLabel(ticket.status);
            return (
              <div
                key={ticket.id}
                onClick={() => handleViewTicket(ticket)}
                style={{
                  padding: '16px',
                  background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                  borderRadius: '12px',
                  border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ 
                      color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280',
                      fontSize: '12px'
                    }}>
                      #{ticket.id}
                    </span>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: statusStyle.bg,
                      color: statusStyle.color
                    }}>
                      {statusStyle.text}
                    </span>
                    {ticket.response_count > 0 && (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        background: 'rgba(59, 130, 246, 0.15)',
                        color: '#60a5fa'
                      }}>
                        <Icon name="chat" color="info" darkMode={darkMode} size={11} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                        {ticket.response_count}
                      </span>
                    )}
                  </div>
                  <span style={{ 
                    fontSize: '12px', 
                    color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af' 
                  }}>
                    {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString('en-US') : ''}
                  </span>
                </div>
                <h4 style={{ 
                  margin: '0 0 4px 0', 
                  fontSize: '15px', 
                  color: darkMode ? 'white' : '#1f2937' 
                }}>
                  {ticket.subject}
                </h4>
                <p style={{ 
                  margin: 0, 
                  fontSize: '13px', 
                  color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {ticket.description}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // Ticket details modal
  const TicketDetailsModal = () => {
    if (!selectedTicket) return null;
    
    const statusStyle = ticketDetails ? getStatusLabel(ticketDetails.status) : {};
    
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
          padding: '24px'
        }}
        onClick={handleCloseDetails}
      >
        <div
          style={{
            background: darkMode ? '#1a1a3e' : '#ffffff',
            border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
            borderRadius: '20px',
            padding: '28px',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {loadingDetails ? (
            <div className="loading">Loading...</div>
          ) : ticketDetails ? (
            <>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                <div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ color: darkMode ? 'rgba(255,255,255,0.5)' : '#6b7280', fontSize: '14px' }}>
                      Ticket #{ticketDetails.id}
                    </span>
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '11px',
                      fontWeight: '600',
                      background: statusStyle.bg,
                      color: statusStyle.color
                    }}>
                      {statusStyle.text}
                    </span>
                  </div>
                  <h2 style={{ margin: 0, fontSize: '20px', color: darkMode ? 'white' : '#1f2937' }}>
                    {ticketDetails.subject}
                  </h2>
                </div>
                <button
                  onClick={handleCloseDetails}
                  style={{
                    background: 'var(--bg-overlay-medium)',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'
                  }}
                >
                  ×
                </button>
              </div>
              
              {/* Description */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  padding: '16px',
                  background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  borderRadius: '12px',
                  color: darkMode ? 'rgba(255,255,255,0.9)' : '#1f2937',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.6'
                }}>
                  {ticketDetails.description}
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af',
                  marginTop: '8px'
                }}>
                Created: {ticketDetails.created_at ? new Date(ticketDetails.created_at).toLocaleString('en-US') : ''}
                </div>
              </div>
              
              {/* Resolution notes */}
              {ticketDetails.resolution_notes && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#4A8C18', fontSize: '14px' }}>
                    <Icon name="success" color="success" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Mark as solution
                  </h4>
                  <div style={{
                    padding: '16px',
                    background: 'rgba(16, 185, 129, 0.1)',
                    borderRadius: '12px',
                    color: darkMode ? 'rgba(255,255,255,0.9)' : '#1f2937',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {ticketDetails.resolution_notes}
                  </div>
                </div>
              )}
              
              {/* Responses */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', fontSize: '14px' }}>
                  Communication ({ticketDetails.responses?.length || 0})
                </h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {(ticketDetails.responses || []).map(response => (
                    <div
                      key={response.id}
                      style={{
                        padding: '16px',
                        background: response.author_role === 'super_admin'
                          ? 'rgba(139, 92, 246, 0.1)'
                          : (darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'),
                        borderRadius: '12px',
                        border: response.author_role === 'super_admin'
                          ? '1px solid rgba(139, 92, 246, 0.3)'
                          : (darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)')
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontWeight: '600', color: darkMode ? 'white' : '#1f2937' }}>
                            {response.author_role === 'super_admin' ? (
                              <>
                                <Icon name="verified" color="primary" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                                Support Team
                              </>
                            ) : 'Te'}
                          </span>
                        </div>
                        <span style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.5)' : '#9ca3af' }}>
                          {response.created_at ? new Date(response.created_at).toLocaleString('en-GB') : ''}
                        </span>
                      </div>
                      <div style={{ 
                        color: darkMode ? 'rgba(255,255,255,0.9)' : '#1f2937',
                        whiteSpace: 'pre-wrap',
                        lineHeight: '1.6'
                      }}>
                        {response.message}
                      </div>
                    </div>
                  ))}
                  
                  {(!ticketDetails.responses || ticketDetails.responses.length === 0) && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '24px', 
                      color: darkMode ? 'rgba(255,255,255,0.4)' : '#9ca3af' 
                    }}>
                      No responses yet — we will get back to you soon.
                    </div>
                  )}
                </div>
              </div>
              
              {/* Add response form */}
              {ticketDetails.status !== 'closed' && ticketDetails.status !== 'resolved' && (
                <div style={{
                  padding: '16px',
                  background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
                  borderRadius: '12px'
                }}>
                  <h4 style={{ margin: '0 0 12px 0', color: darkMode ? 'rgba(255,255,255,0.7)' : '#374151', fontSize: '14px' }}>
                    Send reply
                  </h4>
                  <textarea
                    key={`response-${ticketDetails.id}`}
                    value={responseText}
                    onChange={(e) => {
                      e.stopPropagation();
                      setResponseText(e.target.value);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder="Type your message..."
                    className="form-input"
                    style={{ minHeight: '100px', resize: 'vertical', marginBottom: '12px' }}
                    autoComplete="off"
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={handleAddResponse}
                      disabled={!responseText.trim() || submittingResponse}
                      className="btn btn-primary"
                    >
                      {submittingResponse ? 'Sending...' : 'Send reply'}
                    </button>
                  </div>
                </div>
              )}
              
              {(ticketDetails.status === 'closed' || ticketDetails.status === 'resolved') && (
                <div style={{
                  padding: '16px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '12px',
                  textAlign: 'center',
                  color: '#4A8C18'
                }}>
                  <Icon name="success" color="success" darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  This ticket is closed. If you need more help, please open a new ticket.
                </div>
              )}
            </>
          ) : (
        <div className="error">Failed to load ticket details</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
      border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
      borderRadius: '16px',
      padding: '24px'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: '0 0 8px 0', color: darkMode ? 'white' : '#1f2937', fontSize: '20px' }}>
          <Icon name="support" color="primary" darkMode={darkMode} size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
          Contact & Support
        </h2>
        <p style={{ margin: 0, color: darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280', fontSize: '14px' }}>
          Have a question or issue? Send us a message and we’ll reply shortly.
        </p>
      </div>
      
      {/* Tab navigation */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '24px',
        padding: '4px',
        background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
        borderRadius: '12px'
      }}>
        <button
          onClick={() => setActiveView('new')}
          style={{
            flex: 1,
            padding: '12px',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            background: activeView === 'new' ? '#4A8C18' : 'transparent',
            color: activeView === 'new' ? '#ffffff' : (darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'),
            transition: 'all 0.2s'
          }}
        >
          <Icon name="edit" color={activeView === 'new' ? "primary" : "secondary"} darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
          New ticket
        </button>
        <button
          onClick={() => setActiveView('my-tickets')}
          style={{
            flex: 1,
            padding: '12px',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            background: activeView === 'my-tickets' ? '#4A8C18' : 'transparent',
            color: activeView === 'my-tickets' ? '#ffffff' : (darkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'),
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}
        >
          <Icon name="details" color={activeView === 'my-tickets' ? "primary" : "secondary"} darkMode={darkMode} size={14} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
          My tickets
          {myTickets.length > 0 && (
            <span style={{
              background: activeView === 'my-tickets' ? 'rgba(0,0,0,0.2)' : 'rgba(61, 90, 32, 0.2)',
              padding: '2px 8px',
              borderRadius: '10px',
              fontSize: '12px',
              color: activeView === 'my-tickets' ? '#0a0a1a' : '#3d5a20'
            }}>
              {myTickets.length}
            </span>
          )}
        </button>
      </div>
      
      {error && <div className="error" style={{ marginBottom: '16px' }}>{error}</div>}
      {success && <div className="success" style={{ marginBottom: '16px' }}>{success}</div>}
      
      {/* Content - IMPORTANT: call as functions, NOT as <Components />, to avoid remount on every render */}
      {activeView === 'new' ? NewTicketForm() : MyTicketsList()}
      
      {/* Ticket details modal */}
      {TicketDetailsModal()}
    </div>
  );
}

export default SupportContact;
